"use client"

import * as DialogPrimitive from "@radix-ui/react-dialog"
import {
  type ComponentPropsWithoutRef,
  type ComponentRef,
  forwardRef,
  type HTMLAttributes,
} from "react"
import { cn } from "./cn"

export const Dialog = DialogPrimitive.Root
export const DialogTrigger = DialogPrimitive.Trigger
export const DialogPortal = DialogPrimitive.Portal
export const DialogClose = DialogPrimitive.Close

// z-index values applied via inline `style` rather than Tailwind arbitrary
// values (`z-[60]`, `z-[70]`). Host apps (apps/app, apps/web) run Tailwind v4
// without an `@source` directive pointing at `packages/consent/**`, so any
// arbitrary-value utility we use here is NOT emitted into their final CSS and
// silently falls back to `z-auto`. That previously caused the center
// preference dialog (should be z:70) to render BELOW the sticky banner
// (z-50, which IS present in the host apps and therefore emitted).
// Using `style={{ zIndex: ... }}` sidesteps Tailwind's content scan entirely.
export const CONSENT_Z_OVERLAY = 60
export const CONSENT_Z_CENTER_CONTENT = 70

export const DialogOverlay = forwardRef<
  ComponentRef<typeof DialogPrimitive.Overlay>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, style, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 bg-black/50 backdrop-blur-sm",
      "data-[state=open]:animate-in data-[state=closed]:animate-out",
      "data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0",
      className,
    )}
    style={{ zIndex: CONSENT_Z_OVERLAY, ...style }}
    {...props}
  />
))
DialogOverlay.displayName = "DialogOverlay"

export type DialogContentPosition = "center" | "bottom"

const POSITION_CLASSES: Record<DialogContentPosition, string> = {
  center: cn(
    // Dead-centred, capped at ~85vh so long content scrolls inside rather
    // than extending past the viewport. z-index applied inline — see note
    // above `CONSENT_Z_OVERLAY`.
    "fixed left-1/2 top-1/2 w-[calc(100vw-2rem)] max-w-lg max-h-[calc(100vh-4rem)] -translate-x-1/2 -translate-y-1/2",
    "rounded-xl border border-border",
    "data-[state=open]:zoom-in-95 data-[state=closed]:zoom-out-95",
  ),
  bottom: cn(
    // Bottom sheet: pinned to the viewport bottom on mobile (full width),
    // centered card floating just above the footer on desktop. z-50 is kept
    // as a Tailwind class because that utility IS in the host apps' CSS
    // (used elsewhere), so it gets emitted reliably. Center dialogs layer
    // above via the inline zIndex on overlay + content.
    "fixed inset-x-0 bottom-0 z-50 mx-auto w-full sm:max-w-2xl sm:bottom-6 sm:inset-x-auto sm:left-1/2 sm:-translate-x-1/2",
    "rounded-t-xl border-t border-x border-border sm:rounded-xl sm:border",
    "data-[state=open]:slide-in-from-bottom-4 data-[state=closed]:slide-out-to-bottom-4",
  ),
}

export interface DialogContentProps
  extends ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  position?: DialogContentPosition
}

export const DialogContent = forwardRef<
  ComponentRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(({ className, children, position = "center", style, ...props }, ref) => (
  <DialogPortal>
    {/* The bottom sticky banner is intentionally passive — we skip the
        dimmed overlay so the page behind stays visible and un-obscured. */}
    {position === "center" ? <DialogOverlay /> : null}
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        POSITION_CLASSES[position],
        "bg-background p-6 shadow-lg",
        "flex flex-col gap-4",
        "data-[state=open]:animate-in data-[state=closed]:animate-out",
        "data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0",
        "focus:outline-none",
        className,
      )}
      // Center dialog gets its z-index inline so it layers above the sticky
      // banner's `z-50` without relying on Tailwind arbitrary values. Bottom
      // position keeps the Tailwind `z-50` class from POSITION_CLASSES.
      style={position === "center" ? { zIndex: CONSENT_Z_CENTER_CONTENT, ...style } : style}
      {...props}
    >
      {children}
    </DialogPrimitive.Content>
  </DialogPortal>
))
DialogContent.displayName = "DialogContent"

export const DialogHeader = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex flex-col gap-1.5", className)} {...props} />
)
DialogHeader.displayName = "DialogHeader"

export const DialogFooter = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => (
  <div
    // No default horizontal alignment (each dialog specifies its own via
    // className so Tailwind doesn't arbitrate between competing `justify-*`
    // utilities) and no `mt-*` — DialogContent's `gap-4` handles spacing.
    className={cn("flex flex-col-reverse gap-2 sm:flex-row sm:gap-2", className)}
    {...props}
  />
)
DialogFooter.displayName = "DialogFooter"

export const DialogTitle = forwardRef<
  ComponentRef<typeof DialogPrimitive.Title>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold leading-tight tracking-tight text-foreground", className)}
    {...props}
  />
))
DialogTitle.displayName = "DialogTitle"

export const DialogDescription = forwardRef<
  ComponentRef<typeof DialogPrimitive.Description>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm leading-relaxed text-muted-foreground", className)}
    {...props}
  />
))
DialogDescription.displayName = "DialogDescription"
